<template>
  <client-only>
    <div id="dashboardWrap" ref="dashboardWrap" class="pdfContent">
      <main id="dashboardContent" class="pt0">
        <LazyProSkillManagerCommonBreadCrumb
          :path-name="`${t('skillCertify-candidate', 1)} ${t('skillCertify-candidate-report')}`"
          :assessment-name="reportInfo.title || ''"
          style="top: 0; padding: 13px 40px"
        />
        <LazyProSkillManagerReportBanner
          :user-data="formattedUserData"
          :user-id="uuid"
          :assessment-id="assessmentId"
          :is-pdf-style="true"
          style="padding: 39px 24px;"
        />
        <LazyProSkillManagerReportTab
          :tab-type="ReportTabType.ASSESSMENT"
          :is-visible-analysis-tab="isVisibleAnalysisTab"
          style="top: 0; background: #EAECF3; padding: 0 24px"
        />
        <div class="contentLineWrap" style="padding: 0 24px; margin-bottom: 60px;">
          <div class="containerDash-1480 flex">
            <section id="skillMonitor" style="width: 100%">
              <div class="containerDash" style="padding: 0; margin: 0; max-width: 1480px">
                <div class="skillMonitor">
                  <LazyProSkillManagerReportAssessmentSummary
                    :assessment-id="assessmentId"
                    :assessment-summary="assessmentSummary"
                    :user-nick-name="formattedNickName"
                    :is-unsubmitted="reportInfo.isUnsubmitted"
                  />
                  <section
                    v-for="(trackCandidate, index) in trackSummary"
                    :id="`track-${trackCandidate.trackSummary.trackId}`"
                    :key="trackCandidate.trackSummary.trackId"
                    class="reportItem flex col reportBreakBefore"
                  >
                    <div class="titArea flex">
                      <h3>{{ trackCandidate.trackSummary.trackName }}</h3>
                    </div>
                    <div class="flex col gap-16">
                      <LazyCommonResultTrackSummary
                        :track-candidate-summary="trackCandidate"
                        :is-assessment-detail-report="false"
                        :assessment-id="assessmentId"
                        :user-nick-name="formattedNickName"
                        :is-pdf="true"
                        :is-unsubmitted="reportInfo.isUnsubmitted"
                      />
                      <LazyCommonResultSkillSummary
                        class="reportBreakBefore"
                        :skill-summary="skillSummary[index]"
                        :is-assessment-detail-report="false"
                        :user-nick-name="formattedNickName"
                        :is-pdf="true"
                        :is-unsubmitted="reportInfo.isUnsubmitted"
                      />
                    </div>
                  </section>
                </div>
              </div>
            </section>
          </div>
        </div>
        <template v-if="isVisibleAnalysisTab">
          <LazyProSkillManagerReportTab
              class="reportBreakBefore"
              :tab-type="ReportTabType.ANALYSIS"
              :is-visible-analysis-tab="isVisibleAnalysisTab"
              style="top: 0; background: #EAECF3; padding: 0 24px"
          />
          <div class="contentLineWrap" style="padding: 0 24px; margin-bottom: 60px;">
            <div class="containerDash-1480 flex">
              <section id="skillMonitor" style="width: 100%">
                <div class="containerDash" style="padding: 0; margin: 0; max-width: 1480px">
                  <div class="skillMonitor">
                    <LazyProSkillManagerReportAnalysis
                        :assessment-id="assessmentId"
                        :user-uuid="uuid"
                        :analysis-tasks="analysisTasksData"
                        :assessment-tasks="assessmentTasksData"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </template>
        <LazyProSkillManagerReportTab
          :tab-type="ReportTabType.ACTION"
          :is-visible-analysis-tab="isVisibleAnalysisTab"
          class="reportBreakBefore"
          style="top: 0; background: #EAECF3; padding: 0 24px"
        />
        <div class="contentLineWrap" style="padding: 0 24px">
          <div class="containerDash-1480 flex">
            <section id="skillMonitor" style="width: 100%">
              <div class="containerDash" style="padding: 0; margin: 0; max-width: 1480px">
                <div class="skillMonitor">
                  <LazyReportActionAnalysisResult :action-analysis="candidateActionAnalysis" :is-pdf-style="true"/>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  </client-only>
</template>
<script setup lang="ts">
import {
  LazyCommonResultSkillSummary,
  LazyCommonResultTrackSummary,
  LazyProSkillManagerCommonBreadCrumb,
  LazyProSkillManagerReportAssessmentSummary,
  LazyProSkillManagerReportBanner,
  LazyProSkillManagerReportTab,
  LazyReportActionAnalysisResult,
  LazyProSkillManagerReportAnalysis
} from "#components";
import { useNuxtApp } from "#imports";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import type { SkillSummaryModel } from "~~/package/mapper/report/model/assessment/SkillSummaryModel";
import { ReportDomainType } from "~~/package/mapper/report/model/ReportDomainType";
import { useReportService } from "~/composables/service/useReportService";
import {
  AssessmentCandidateSummaryModel
} from "~~/package/mapper/report/model/candidate/AssessmentCandidateSummaryModel";
import { TrackCandidateSummaryModel } from "~~/package/mapper/report/model/candidate/TrackCandidateSummaryModel";
import { useProSkillManagerService } from "~/composables/service/useProSkillManagerService";
import { CandidateActionAnalysisModel } from "~~/package/mapper/report/model/candidate/CandidateActionAnalysisModel";
import { ProSkillMangerReportModel } from "~~/package/mapper/proSkillManager/model/report/ProSkillManagerReportModel";
import { ReportTabType } from "~~/package/mapper/report/model/ReportTabType";
import { useCertifyStaticAnalysisService } from "~/composables/service/useCertifyStaticAnalysisService";
import {
  ProSkillManagerActionUserInfoModel
} from "~~/package/mapper/proSkillManager/model/actions/ProSkillManagerActionUserInfoModel";


const { t } = useI18n();

const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);

const formatKoreanName = (name: string | null): string => {
  if (!name) return '';
  if (lang.value === 'KO' && name.includes(' ')) {
    const parts = name.split(' ');
    return parts.reverse().join('');
  }
  return name;
};

const formattedNickName = computed(() => formatKoreanName(reportInfo.value.userInfo.nickName));

const formattedUserData = computed(() => {
  const userInfo = reportInfo.value.userInfo;
  if (lang.value === 'KO' && userInfo.nickName?.includes(' ')) {
    return ProSkillManagerActionUserInfoModel.create({
      ...userInfo,
      nickName: formatKoreanName(userInfo.nickName)
    });
  }
  return userInfo;
});
const route = useRoute();

const proSkillManagerService = useProSkillManagerService();
const reportService = useReportService();
const certifyStaticAnalysisService = useCertifyStaticAnalysisService();

const isLoading = ref(true);
const uuid = route.query.uuid as string;

const reportInfo = ref<ProSkillMangerReportModel>(new ProSkillMangerReportModel());
const assessmentSummary = ref<AssessmentCandidateSummaryModel>(new AssessmentCandidateSummaryModel());
const trackSummary = ref<TrackCandidateSummaryModel[]>([]);
const skillSummary = ref<SkillSummaryModel[]>([]);
const candidateActionAnalysis = ref<CandidateActionAnalysisModel>(new CandidateActionAnalysisModel());
const analysisTasksData = ref<Array<{taskId: string, files: string[]}>>([]);
const assessmentTasksData = ref<Array<{trackTitle: string, trackLevelTitle: string, taskIds: number[]}>>([]);

const assessmentId = computed(() => parseInt(route.params.assessmentId as string));

const isVisibleAnalysisTab = computed(() => {
  return analysisTasksData.value.length > 0;
});

const checkAnalysisSummaryFiles = async () => {
  try {
    // S3에서 분석 파일 조회
    const s3TasksData = await certifyStaticAnalysisService.getAllTasksAnalysis(
      assessmentId.value,
      uuid
    );

    // API에서 assessment tasks 조회
    const apiTasksData = await proSkillManagerService.getAssessmentTasks(assessmentId.value);
    assessmentTasksData.value = apiTasksData; // 저장해서 prop으로 전달
    
    // API에서 조회한 모든 taskId 목록
    const validTaskIds = new Set<string>();
    apiTasksData.forEach(track => {
      track.taskIds.forEach(taskId => {
        validTaskIds.add(String(taskId));
      });
    });

    // S3의 taskId가 API taskIds와 매칭되는 것만 필터링
    const matchedTasks = s3TasksData.filter(task => validTaskIds.has(task.taskId));

    analysisTasksData.value = matchedTasks;
  } catch (error) {
    console.error('Failed to load analysis data:', error);
    analysisTasksData.value = [];
    assessmentTasksData.value = [];
  }
};

const initData = async () => {
  await Promise.all([
      proSkillManagerService.getProSkillManagerReport(lang.value, uuid, assessmentId.value),
      reportService.getReportCandidateAssessmentSummary(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportCandidateTrackSummary(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportCandidateSkillSummary(lang.value, uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportCandidateActionAnalysis(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value)
    ]
  ).then(resolve => {
    reportInfo.value = resolve[0];
    assessmentSummary.value = resolve[1];
    trackSummary.value = resolve[2];
    skillSummary.value = resolve[3];
    candidateActionAnalysis.value = resolve[4];
  });

  // S3에서 분석 파일 확인
  await checkAnalysisSummaryFiles();

  isLoading.value = false;
};

onMounted(() => {
  initData();
});
</script>

<style>
@import "~/assets/scss/business_style.scss";

.pdfContent .reportBreakBefore {
  break-before: page !important;
  page-break-before: always !important;
}

.pdfContent .board__detail {
  break-inside: avoid;
}
.pdfContent .reportBreakInside {
  break-inside: avoid;
  page-break-inside: avoid; /* 구형 호환 */
}
.pdfContent .reportBreakInsideSkip {
  break-inside: auto !important;
  page-break-inside: auto !important; /* 구형 호환 */
}
</style>